import { Controller } from '@hotwired/stimulus'
import { debounce, divide, find, includes, indexOf, reject, sortBy } from 'lodash'

export default class extends Controller {

  static targets = [
    'addCheckBtn',
    'cancelSplitBtn',
    'checkForm',
    'container',
    'maxNotification',
    'remainingTotal',
    'splitAddContainer',
    'splitPaymentBtn',
  ]

  initialize() {
    this.MAX_CHECKS = parseInt(this.containerTarget.dataset.maxChecks)
    this.CHECK_DISPLAY_CLASSES = []
    for (let index = 1; index <= this.MAX_CHECKS; index += 1) {
      this.CHECK_DISPLAY_CLASSES.push(`order-${index}`)
    }
    this.checkoutContainer = document.querySelector('#checkout-container')

    this.splitPayment = debounce(this.splitPayment, 300)
    this.cancelSplit = debounce(this.cancelSplit, 300)
    this.setIsPaymentFormComplete = debounce(this.setIsPaymentFormComplete, 100)
    this.updateRemainingAmount = debounce(this.updateRemainingAmount, 100)
    this.dispatchPaymentUpdate = debounce(this.dispatchPaymentUpdate, 100)
  }

  connect() {
    this.isPaymentFormComplete = false
    this.checks = [{ originalIndex: 0, order: 0 }]
    this.updateAndDisplayChecks()

    this.checkoutContainer.addEventListener('orderLoaded', this.onOrderLoaded)
    this.checkoutContainer.addEventListener('checkUpdate', this.onCheckUpdate)
    this.checkoutContainer.addEventListener('removeCheck', this.onRemoveCheck)
  }

  onOrderLoaded = event => {
    this.order = event?.detail?.order
  }

  dispatchSplitMode(activeIndex) {
    const detail = { splitModeActive: this.splitModeActive, activeIndex }
    const splitModeUpdateEvent = new CustomEvent('splitModeUpdate', { detail })
    this.checkoutContainer.dispatchEvent(splitModeUpdateEvent)
  }

  dispatchPaymentUpdate() {
    const detail = {
      checks: this.checks,
      isPaymentFormComplete: this.isPaymentFormComplete,
    }
    const paymentUpdateEvent = new CustomEvent('paymentUpdate', { detail })
    this.checkoutContainer.dispatchEvent(paymentUpdateEvent)
  }

  splitPayment = _event => {
    const currentCheck = this.checks[0]
    this.checks[0].amountCents = 0
    const newCheckIndex = (currentCheck.originalIndex + 2) > this.MAX_CHECKS ? 0 : currentCheck.originalIndex + 1
    this.checks = [currentCheck, { originalIndex: newCheckIndex, order: 1, complete: false, amountCents: 0 }]
    this.splitModeActive = true
    this.isPaymentFormComplete = false
    this.dispatchSplitMode(currentCheck.originalIndex)
    this.remainingTotalTarget.innerText = `$ ${ divide(this.order.total_cents, 100) }`
    this.dispatchPaymentUpdate()
    this.splitAddContainerTarget.hidden = false
    this.splitPaymentBtnTarget.hidden = true
    this.cancelSplitBtnTarget.hidden = false
    this.updateAndDisplayChecks()
    this.updateRemainingAmount()
  }

  cancelSplit = _event => {
    this.splitModeActive = false
    const firstCheck = find(this.checks, ['order', 1])
    this.checks = [firstCheck]
    this.updateAndDisplayChecks()
    this.splitAddContainerTarget.hidden = true
    this.splitPaymentBtnTarget.hidden = false
    this.cancelSplitBtnTarget.hidden = true
    this.addCheckBtnTarget.disabled = false
    this.maxNotificationTarget.innerText = ''
    this.setIsPaymentFormComplete()
    this.dispatchSplitMode(firstCheck.originalIndex)
    this.dispatchPaymentUpdate()
    this.updateRemainingAmount()
  }

  updateCheckInfo() {
    const paymentTotal = this.checks.reduce((acc, check) => acc + check.amountCents, 0)
    const orderedChecks = sortBy([...this.checks], 'order')
    this.checks = orderedChecks.map((check, index) => {
      check.order = index + 1
      if (this.order?.total_cents) {
        const maxAmount = this.order.total_cents - paymentTotal + check.amountCents
        check.maxAmount = maxAmount
      }
      return check
    })
    const detail = { checks: this.checks }
    const checksOrderedEvent = new CustomEvent('checksOrdered', { detail })
    this.checkoutContainer.dispatchEvent(checksOrderedEvent)
  }

  getCurrentIndexOfCheck(originalIndex) {
    const currentIndices = this.checks.map(check => check.originalIndex)
    return indexOf(currentIndices, originalIndex)
  }

  displayChecks() {
    this.checkFormTargets.forEach(form => {
      this.CHECK_DISPLAY_CLASSES.forEach(cssClass => {
        form.classList.remove(cssClass)
      })
      const originalIndex = parseInt(form.dataset.originalIndex)
      const check = find(this.checks, ['originalIndex', originalIndex])
      if (check) {
        form.hidden = false
        form.classList.remove('order-last')
        form.classList.add(`order-${check.order}`)
      } else {
        form.hidden = true
        form.classList.add('order-last')
      }
    })
  }

  updateAndDisplayChecks() {
    this.updateCheckInfo()
    this.displayChecks()
  }

  onCheckUpdate = ({ detail }) => {
    const { originalIndex } = detail
    const currentIndex = this.getCurrentIndexOfCheck(originalIndex)
    if (currentIndex >= 0) {
      this.checks[currentIndex] = Object.assign(this.checks[currentIndex], detail)
    }

    this.updateAndDisplayChecks()
    this.setIsPaymentFormComplete()
    this.dispatchPaymentUpdate()
    this.updateRemainingAmount()
  }

  setIsPaymentFormComplete() {
    const checksComplete = this.checks.reduce((acc, check) => acc && check.complete, true)
    const paymentTotal = this.checks.reduce((acc, check) => acc + check.amountCents, 0)
    const validAmounts = this.order?.total_cents >= paymentTotal && paymentTotal >= 0
    this.isPaymentFormComplete = checksComplete && validAmounts
  }

  addCheck = ({ _detail }) => {
    const nextOrder = this.checks.length
    if (nextOrder >= this.MAX_CHECKS) {
      return
    }
    this.maxNotificationTarget.innerText = ''
    const activeIndices = this.checks.map(check => check.originalIndex)
    const possibleIndices = [...Array(this.MAX_CHECKS).keys()]
    const nextIndex = find(possibleIndices, index => !includes(activeIndices, index))
    this.checks = [...this.checks, { originalIndex: nextIndex, order: nextOrder, complete: false, title: `Check ${ nextOrder + 1 }` }]
    this.setIsPaymentFormComplete()
    this.updateAndDisplayChecks()
    this.dispatchPaymentUpdate()
    if (this.checks.length >= this.MAX_CHECKS) {
      this.addCheckBtnTarget.disabled = true
      this.maxNotificationTarget.innerText = ` (max ${this.MAX_CHECKS})`
    }
  }

  onRemoveCheck = ({ detail }) => {
    const { originalIndex } = detail
    this.addCheckBtnTarget.disabled = false
    this.maxNotificationTarget.innerText = ''
    this.checks = reject([...this.checks], ['originalIndex', originalIndex])
    this.updateAndDisplayChecks()
    this.setIsPaymentFormComplete()
    this.dispatchPaymentUpdate()
    this.updateRemainingAmount()
  }

  updateRemainingAmount() {
    const totalCents = this.checks.map(check => check.amountCents).reduce((acc, cents) => acc + (cents || 0), 0)
    const remainingDollars = divide(this.order.total_cents - totalCents, 100)
    this.remainingTotalTarget.innerText = `$ ${ remainingDollars.toFixed(2) }`
  }
}
